import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Button } from '@frontend/design-system';
import { SIGNATURE_PAD_WIDTH } from './constants';
import { SignaturePadCanvas, SignaturePadCanvasProps, SignaturePadHandle } from './signature-pad';
import { drawAreaActionsStyle, drawAreaStyle } from './signature.styles';

/** Signature pad plus its "Clear Signature" action, shared by the eSign and PDF signing flows. */
export const SignatureDrawArea = forwardRef<SignaturePadHandle, SignaturePadCanvasProps>(
  ({ onDrawingChange, className, ...padProps }, ref) => {
    const { t } = useTranslation('digital-forms-experience');
    const padRef = useRef<SignaturePadHandle>(null);
    const [hasDrawing, setHasDrawing] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        clear: () => padRef.current?.clear(),
        isEmpty: () => padRef.current?.isEmpty() ?? true,
        exportTrimmed: (padding) => padRef.current?.exportTrimmed(padding) ?? null,
      }),
      []
    );

    return (
      <div css={[drawAreaStyle, { maxWidth: SIGNATURE_PAD_WIDTH }]} className={className}>
        <SignaturePadCanvas
          ref={padRef}
          {...padProps}
          onDrawingChange={(drawn) => {
            setHasDrawing(drawn);
            onDrawingChange?.(drawn);
          }}
        />
        <div css={drawAreaActionsStyle}>
          <Button
            variant='tertiary'
            size='small'
            iconName='refresh'
            disabled={!hasDrawing || padProps.disabled}
            onClick={() => padRef.current?.clear()}
          >
            {t('Clear Signature')}
          </Button>
        </div>
      </div>
    );
  }
);
SignatureDrawArea.displayName = 'SignatureDrawArea';
